import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';

// Import components
import Navigation from './components/Navigation/Navigation';
import Hero from './components/Hero/Hero';
import Services from './components/Services/Services';
import About from './components/About/About';
import Contact from './components/Contact/Contact';
import Footer from './components/Footer/Footer';
import RemoteAccess from './components/RemoteAccess/RemoteAccess';
import RemoteMonitoring from './components/RemoteMonitoring/RemoteMonitoring';
import CustomSoftware from './components/CustomSoftware/CustomSoftware';
import Resources from './components/Resources/Resources';
import ArticleView from './components/Resources/ArticleView';
import Support from './components/Support/Support';
import CaseStudies from './components/CaseStudies/CaseStudies';
import Branding from './components/Branding/Branding';
import NotFound from './components/NotFound/NotFound';

// Import styles
import './styles/main.scss';
import './App.css';

/**
 * SEO component - Manages metadata for SEO optimization
 * 
 * Renders appropriate title, description, and other metadata tags
 * based on the current route
 * 
 * @param {Object} props - Component properties
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {string} props.keywords - Comma separated keywords
 * @param {string} props.canonicalUrl - Canonical URL for the page
 * @param {string} props.ogImage - Open Graph image URL
 * @returns {JSX.Element} The SEO component with metadata tags
 */
const SEO = ({ 
  title = "MET Connect® - Secure Remote Access Solutions", 
  description = "MET Connect® offers comprehensive remote access, monitoring, and custom software solutions for your industrial control systems.",
  keywords = "remote access, industrial control systems, SCADA, PLC, remote monitoring, custom software, ladder logic",
  canonicalUrl,
  ogImage = `${process.env.PUBLIC_URL}/images/Logo1.png`
}) => {
  const { pathname } = useLocation();
  const baseUrl = process.env.PUBLIC_URL || "https://met-connect.com";
  const fullUrl = canonicalUrl || `${baseUrl}${pathname}`;
  
  return (
    <>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Canonical Link */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      
      {/* Color Theme */}
      <meta name="theme-color" content="#0056b3" />
    </>
  );
};

/**
 * ScrollToTop - Component that scrolls to top on route change or reload
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

/**
 * ScrollToSection - Helper component to handle scrolling after navigation
 */
const ScrollToSection = () => {
  const { state } = useLocation();
  
  useEffect(() => {
    // Check if we have a scrollTo parameter in the router state
    if (state && state.scrollTo) {
      const section = document.getElementById(state.scrollTo);
      
      if (section) {
        // Add a small delay to ensure the page has fully rendered
        setTimeout(() => {
          // Use scrollIntoView with smooth behavior
          section.scrollIntoView({ 
            behavior: 'smooth',
            // The scroll-margin-top CSS property handles the offset,
            // so we don't need to manually calculate it here
          });
        }, 300); // Increased delay to ensure page is fully loaded
      }
    }
  }, [state]);
  
  return null;
};

/**
 * RouteMetadata - Component that renders appropriate metadata for each route
 * 
 * @returns {JSX.Element} The metadata tags for the current route
 */
const RouteMetadata = () => {
  const { pathname } = useLocation();
  
  switch (pathname) {
    case '/':
      return (
        <SEO 
          title="MET Connect® - Secure Industrial Remote Access Solutions"
          description="MET Connect® provides secure remote access, real-time monitoring, and custom software development for industrial control systems."
          keywords="industrial remote access, SCADA monitoring, PLC programming, industrial IoT, secure remote connectivity"
        />
      );
    case '/remote-access':
      return (
        <SEO 
          title="Remote Access Solutions | MET Connect®"
          description="Enterprise-grade secure remote access solutions for industrial control systems with no static IP requirements and multi-factor authentication."
          keywords="secure remote access, VPN, industrial networks, remote connectivity, SCADA access"
        />
      );
    case '/remote-monitoring':
      return (
        <SEO 
          title="Remote Monitoring Services | MET Connect®"
          description="Real-time monitoring and alerting services for industrial control systems. Track performance metrics and receive proactive alerts."
          keywords="industrial monitoring, SCADA monitoring, real-time alerts, equipment monitoring, predictive maintenance"
        />
      );
    case '/custom-software':
      return (
        <SEO 
          title="Custom Industrial Software Development | MET Connect®"
          description="Specialized PLC, SCADA, and ladder logic programming. Custom software solutions for industrial automation and control systems."
          keywords="PLC programming, SCADA development, ladder logic, HMI design, industrial automation software"
        />
      );
    case '/resources':
      return (
        <SEO 
          title="Knowledge Resources | MET Connect®"
          description="Access guides, tutorials, and best practices for remote access, monitoring, and industrial control system management."
          keywords="industrial control guides, SCADA tutorials, remote access documentation, monitoring best practices"
        />
      );
    case '/support':
      return (
        <SEO 
          title="Technical Support | MET Connect®"
          description="Expert technical support for MET Connect® services. Get assistance with setup, configuration, and troubleshooting."
          keywords="industrial control support, remote access support, technical assistance, troubleshooting"
        />
      );
    default:
      if (pathname.startsWith('/knowledge/')) {
        return (
          <SEO 
            title="Knowledge Article | MET Connect®"
            description="Educational resources and technical documentation from MET Connect®'s knowledge center."
            keywords="industrial control systems, SCADA, PLC, technical documentation, guides"
          />
        );
      }
      return (
        <SEO />
      );
  }
};

/**
 * App Component - Main entry point for the MET Connect® website
 * 
 * Handles routing and layout for the entire application
 * 
 * @returns {JSX.Element} The App component
 */
function App() {
  return (
    <Router>
      <div className="App">
        <RouteMetadata />
        <ScrollToTop />
        <Navigation />
        <ScrollToSection />
        
        <Routes>
          {/* Home Page Route */}
          <Route path="/" element={
            <>
              <Hero />
              <Services id="services" />
              <CaseStudies id="case-studies" />
              <About />
              <Contact />
            </>
          } />
          
          {/* Remote Access Page Route */}
          <Route path="/remote-access" element={
            <RemoteAccess />
          } />
          
          {/* Remote Monitoring Page Route */}
          <Route path="/remote-monitoring" element={
            <RemoteMonitoring />
          } />
          
          {/* Custom Software Page Route */}
          <Route path="/custom-software" element={
            <CustomSoftware />
          } />
          
          {/* Resources Page Route */}
          <Route path="/resources" element={
            <Resources />
          } />
          
          {/* Knowledge Articles Routes */}
          <Route path="/knowledge/:articleId" element={
            <ArticleView />
          } />
          
          {/* Support Page Route */}
          <Route path="/support" element={
            <Support />
          } />
          
          {/* Branding Page Route - not included in navigation */}
          <Route path="/branding" element={
            <Branding />
          } />
          
          {/* 404 Not Found Route */}
          <Route path="*" element={
            <NotFound />
          } />
        </Routes>
        
        <Footer />
      </div>
    </Router>
  );
}

export default App;
